'use client';

import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import * as v from 'valibot';
import { TextField } from '@/components/ui/textField';
import { priceSchema } from '@/features/products/components/list/filters/priceFilter/productPriceFilter.schema';
import { productListUpdateFilters } from '@/features/products/state/list/actions';
import {
  useProductListDispatch,
  useProductListLoading,
  useProductListMaxProductPrice,
  useProductListSelectedFilters
} from '@/features/products/state/list/context';
import { listingTranslate } from '@/i18n/translations/listingTranslations';

export const ProductPriceFilter = ({ isMobile }: { isMobile?: boolean }) => {
  // const { state: productListState, dispatch } = useProductList();

  const dispatch = useProductListDispatch();
  const selectedFilters = useProductListSelectedFilters();
  const maxProductPrice = useProductListMaxProductPrice();
  const isProductListLoading = useProductListLoading();

  const convertPriceRange = () => {
    if (selectedFilters['variant.price']) {
      const [range] = selectedFilters['variant.price'];
      const [min, max] = (range as string)
        .split(',')
        .map((price) => Number(price.replace('gte:', '').replace('lte:', '')));
      return [min / 100, max / 100];
    }
    return [0, maxProductPrice / 100];
  };

  const [isUserInputCTA, setIsUserInputCTA] = useState<boolean>(false);
  const [inputValues, setInputValues] = useState<(number | '')[]>([0, 20000]);
  const [priceRange, setDebouncedPriceRange] = useDebounce<(number | '')[] | ''>('', 1000);
  const isInvalid = !v.safeParse(priceSchema, inputValues).success;

  useEffect(() => {
    setInputValues(convertPriceRange());
  }, []);

  useEffect(() => {
    setInputValues(convertPriceRange());
  }, [maxProductPrice]);

  useEffect(() => {
    setDebouncedPriceRange(inputValues);
  }, [inputValues]);

  useEffect(() => {
    if (isUserInputCTA) {
      setIsUserInputCTA(false);
      const minPrice = priceRange[0] === '' ? 0 : priceRange[0];
      const maxPrice =
        priceRange[1] === '' || priceRange[1] === 0 ? maxProductPrice / 100 : priceRange[1];

      const convertedPriceRange = convertPriceRange();
      const isDifferent =
        minPrice !== convertedPriceRange[0] || maxPrice !== convertedPriceRange[1];
      const isValid = v.safeParse(priceSchema, [minPrice, maxPrice]).success;

      if (isDifferent && isValid && typeof minPrice === 'number' && typeof maxPrice === 'number') {
        dispatch(
          productListUpdateFilters({
            filterCategoryCode: 'variant.price',
            code: `gte:${Math.round(minPrice * 100)},lte:${Math.round(maxPrice * 100)}`
          })
        );
      }
    }
  }, [priceRange, dispatch]);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    setIsUserInputCTA(true);
    const newValue = event.target.value;
    const newValues = [...inputValues];
    newValues[index] = newValue === '' ? '' : Number(newValue);
    setInputValues(newValues);
  };

  return (
    <div className="px-4 py-2 mt-4 w-full">
      <p className="mb-2 font-bold">{listingTranslate('filters.price')}</p>
      <div className="flex flex-row gap-3 mt-2 justify-between">
        <TextField
          disabled={isProductListLoading}
          error={isInvalid}
          value={inputValues[0]}
          onChange={(e) => handleInputChange(e, 0)}
          size={isMobile ? 'medium' : 'small'}
          type="number"
          className="[&_input::-webkit-outer-spin-button]:hidden [&_input::-webkit-inner-spin-button]:hidden [&_input]:[-moz-appearance:textfield] w-full"
          endAdornment={
            <p
              className={
                isProductListLoading ? 'text-(--mui-palette-text-disabled)' : 'text-inherit'
              }
            >
              {listingTranslate('filters.price.currency')}
            </p>
          }
        />
        <p
          className={`self-center ${isProductListLoading ? 'text-(--mui-palette-text-disabled)' : 'text-inherit'}`}
        >
          -
        </p>
        <TextField
          disabled={isProductListLoading}
          error={isInvalid}
          value={inputValues[1]}
          placeholder={`${maxProductPrice / 100}`}
          onChange={(e) => handleInputChange(e, 1)}
          size={isMobile ? 'medium' : 'small'}
          type="number"
          endAdornment={
            <p
              className={
                isProductListLoading ? 'text-(--mui-palette-text-disabled)' : 'text-inherit'
              }
            >
              {listingTranslate('filters.price.currency')}
            </p>
          }
          className="[&_input::-webkit-outer-spin-button]:hidden [&_input::-webkit-inner-spin-button]:hidden [&_input]:[-moz-appearance:textfield] w-full"
        />
      </div>
      {isInvalid && (
        <span className="mt-1 block text-xs leading-[166%] font-normal text-(--mui-palette-error-main)">
          {listingTranslate('filters.price.error.invalid_range')}
        </span>
      )}
    </div>
  );
};
